/**
 * Requirement realization — M7 brought to the screen (scenarios doc §7-1).
 *
 * M7's finding (docs/reports/generation-variance-2026-08-07/
 * 01-PREREGISTRATION-M7-SILENT-REQUIREMENT-DROP.md): declared spec
 * requirements evaporate from generated output while every gauge stays
 * green — 0.5583 realization on the frozen fwd2 denominator. The research
 * scorer is spec-frozen (R1–R12 belong to ONE spec) and must not be
 * presented as a general metric, so this module does what M7's own
 * discipline allows for product UI: it enumerates THIS app's declared
 * requirements from the intake decisions the user actually made, checks
 * each with a mechanical structural predicate over the generated block
 * documents, and shows which survived and which evaporated.
 *
 * M7's honesty rules carried over verbatim:
 *   - Predicates are STRUCTURAL, not semantic. "Realized" means the
 *     requirement has a place in the output — not that it behaves.
 *   - Only requirements with a mechanical predicate enter the denominator.
 *     Unmeasurable declarations (e.g. legacy keep/improve without the
 *     legacy source at hand) are NOT silently counted either way.
 *   - No declared requirements → null, never an empty-denominator 100%.
 */
import type { BlockDef } from '@cogo/types-v2'
import type { IntakeDecisions } from '@cogo/agentic-generator/multiblock/intake-decisions'

export type RequirementKind =
  | 'visible-field'
  | 'internal-field'
  | 'label-verbatim'
  | 'standard-term'

export interface RequirementRealizationItem {
  id: string
  kind: RequirementKind
  label: string
  realized: boolean
}

export interface RequirementRealizationView {
  items: RequirementRealizationItem[]
  realized: number
  total: number
}

function collectStrings(value: unknown, out: string[]): void {
  if (typeof value === 'string') {
    out.push(value)
    return
  }
  if (Array.isArray(value)) {
    for (const entry of value) collectStrings(entry, out)
    return
  }
  if (value && typeof value === 'object') {
    for (const entry of Object.values(value as Record<string, unknown>)) {
      collectStrings(entry, out)
    }
  }
}

/** Word-boundary-ish token presence: `status` must not match `statusBar…`. */
function tokenPresent(blob: string, token: string): boolean {
  if (!token) return false
  const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(`(^|[^A-Za-z0-9_])${escaped}([^A-Za-z0-9_]|$)`).test(blob)
}

interface Surfaces {
  /** ui.json only — what the screen shows (leak surface, W14/W16 semantics). */
  visibleBlob: string
  /** ui.json + data.json — where a displayed field may be wired. */
  displayBlob: string
  /** Exact string values from ui.json (labels, verbatim text). */
  visibleStrings: Set<string>
}

function buildSurfaces(blocks: Record<string, BlockDef>): Surfaces {
  const uiDocs: unknown[] = []
  const dataDocs: unknown[] = []
  for (const block of Object.values(blocks)) {
    if (block?.ui !== undefined) uiDocs.push(block.ui)
    if (block?.data !== undefined) dataDocs.push(block.data)
  }
  const visibleBlob = JSON.stringify(uiDocs)
  const displayBlob = JSON.stringify([...uiDocs, ...dataDocs])
  const strings: string[] = []
  collectStrings(uiDocs, strings)
  return {
    visibleBlob,
    displayBlob,
    visibleStrings: new Set(strings.map((s) => s.trim()).filter(Boolean)),
  }
}

/**
 * Measure this app's declared requirements against its generated blocks.
 * Returns null when nothing measurable was declared — a missing denominator
 * is reported as absence, never as success.
 */
export function measureRequirementRealization(input: {
  blocks: Record<string, BlockDef> | null | undefined
  intakeDecisions: IntakeDecisions | null | undefined
}): RequirementRealizationView | null {
  const decisions = input.intakeDecisions ?? null
  const blocks = input.blocks ?? null
  if (!decisions || !blocks || Object.keys(blocks).length === 0) return null

  const surfaces = buildSurfaces(blocks)
  const items: RequirementRealizationItem[] = []

  const fv = decisions.fieldVisibility
  if (fv) {
    const visible = (['input', 'output', 'common', 'unknown'] as const).flatMap(
      (section) => fv.visible[section] ?? [],
    )
    for (const field of visible) {
      items.push({
        id: `visible:${field}`,
        kind: 'visible-field',
        label: `필드 표시 — ${field}`,
        realized: tokenPresent(surfaces.displayBlob, field),
      })
    }
    for (const field of fv.internal) {
      items.push({
        id: `internal:${field}`,
        kind: 'internal-field',
        label: `화면 미노출 — ${field}`,
        realized: !tokenPresent(surfaces.visibleBlob, field),
      })
    }
  }

  const labels = decisions.screenDesign?.labelsVerbatim ?? []
  for (const label of labels) {
    items.push({
      id: `label:${label}`,
      kind: 'label-verbatim',
      label: `설계서 라벨 그대로 — "${label}"`,
      realized: surfaces.visibleStrings.has(label.trim()),
    })
  }

  const terms = decisions.referenceExtraction?.standardTerms ?? []
  for (const term of terms) {
    items.push({
      id: `term:${term}`,
      kind: 'standard-term',
      label: `표준 용어 사용 — "${term}"`,
      realized: surfaces.visibleStrings.has(term.trim()) ||
        tokenPresent(surfaces.visibleBlob, term),
    })
  }

  if (items.length === 0) return null
  return {
    items,
    realized: items.filter((item) => item.realized).length,
    total: items.length,
  }
}
